import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import tmdbApi from "../../../api/tmdbApi";
import OpenSeadragon from "openseadragon";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { fromBlob } from "geotiff";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SweetAlert from "react-bootstrap-sweetalert";
import Header from "../../../components/dashboard/header";
import Footer from "../../../components/dashboard/footer";
import SideMenu from "../../student/SideMenu";
import Loader from "../../../components/loader";
import SessionPopup from "../../SessionPopup";
import { contentContext } from "../../../context/contentContext";
import { Uploader } from "../../../utils/upload";
import progessIcon from "../../../assets/images/gifs/process-icon.gif";
import loadingIcon from "../../../assets/images/gifs/uploading-icon.gif";
import errorSvg from "../../../assets/images/gifs/errorSvg.svg";
import deepZoom from "../../../assets/images/icons/Dna-dz.svg";

let { lambda, appname } = window.app;

const STALE_TTL_MS = 4 * 60 * 60 * 1000;
const MAX_PIXELS = 10_000_000_000;
const UPLOADER_BASE_URL = "https://qih3mm5tl8.execute-api.ap-south-1.amazonaws.com/prod/";

const STATUS_COLORS = {
    "Draft":                       { background: "#6c757d" },
    "Submitted":                   { background: "#0d6efd" },
    "Pending Payment":             { background: "#dc3545" },
    "Payment Received":            { background: "#198754" },
    "Under Lab Review":            { background: "#0dcaf0", color: "#000" },
    "Assigned to Pathologist":     { background: "#ffc107", color: "#000" },
    "Additional Test Requested":   { background: "#fd7e14" },
    "Additional Test In Progress": { background: "#6f42c1" },
    "Reassigned to Pathologist":   { background: "#20c997", color: "#000" },
    "Completed":                   { background: "#198754" },
    "Converted to Learning Case":  { background: "#0a58ca" },
};

// ── helpers ──────────────────────────────────────────────────────────────────

function getExtension(filename) {
    if (!filename) return null;
    const ext = filename.split(".").pop().toLowerCase();
    return ext === "tiff" || ext === "tif" ? ".tif,.tiff" : ".jpeg,.jpg,.png";
}

function isStaleUpload(file) {
    if (!file || file.uploadStatus !== "INPROGRESS") return false;
    const lastSeen = new Date(file.lastSeenAt || file.fileCreated || Date.now()).getTime();
    return Date.now() - lastSeen > STALE_TTL_MS;
}

function checkStuckUploads(file) {
    if (!file) return false;
    const created = new Date(file.fileCreated);
    const diffMins = (Date.now() - created) / 1000 / 60;
    return file.uploadStatus === "INPROGRESS" && diffMins > 30;
}

async function getFileDimensions(file) {
    const name = file.name?.toLowerCase() || "";
    if (name.endsWith(".tif") || name.endsWith(".tiff")) {
        const tiff = await fromBlob(file);
        const image = await tiff.getImage();
        return { width: image.getWidth(), height: image.getHeight() };
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.width, height: img.height }); };
        img.onerror = reject;
        img.src = url;
    });
}

// ─────────────────────────────────────────────────────────────────────────────

export default function LabBiopsyEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const labUserId = localStorage.getItem("userId");

    const { GetTimeActivity, setIsUploading, setIsUploadingFailed } = useContext(contentContext);

    // ── case data ───────────────────────────────────────────────────────────
    const [caseDetails, setCaseDetails] = useState(null);
    const [imageCloudfront, setImageCloudfront] = useState("");
    const [resourceBucket, setResourceBucket] = useState("");

    // ── DZI / OpenSeadragon ─────────────────────────────────────────────────
    const viewerRef = useRef(null);
    const transformRef = useRef(null);
    const intervalRef = useRef(null);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [dziUrl, setDziUrl] = useState("");
    const [dziUrlFile, setDziUrlFile] = useState("");
    const [dzipopupImage, setDZIPopupImage] = useState("");
    const [isPopupLoading, setPopupLoading] = useState(false);
    const [viewer, setViewer] = useState(null);
    const [dziImageList, setDziImageList] = useState([]);
    const [dziCurrentIndex, setDziCurrentIndex] = useState(0);
    const [isDziLoading, setIsDziLoading] = useState(false);
    const [isDziProcessing, setIsDziProcessing] = useState(false);
    const [rotation, setRotation] = useState(0);

    // ── histology images ────────────────────────────────────────────────────
    const [histologyImageFiles, setHistologyImageFiles] = useState([]);
    const [histologyImageUrls, setHistologyImageUrls] = useState(null);
    const [histologyImageIndex, setHistologyImageIndex] = useState(0);
    const [uploadedHistologyFiles, setUploadedHistologyFiles] = useState([]);
    const [fileDimension, setFileDimension] = useState({});

    // ── clinical photos ─────────────────────────────────────────────────────
    const [clinicalImageFiles, setClinicalImageFiles] = useState([]);
    const [clinicalImageUrls, setClinicalImageUrls] = useState(null);

    // ── pathologist ─────────────────────────────────────────────────────────
    const [pathologistList, setPathologistList] = useState([]);
    const [selectedPathologistId, setSelectedPathologistId] = useState("");

    // ── dermatologist ────────────────────────────────────────────────────────
    const [dermatologistList, setDermatologistList] = useState([]);
    const [selectedDermatologist, setSelectedDermatologist] = useState(null);

    // ── biopsy type ──────────────────────────────────────────────────────────
    const [selectedBiopsyType, setSelectedBiopsyType] = useState(null);
    const [biopsyTypeOptions, setBiopsyTypeOptions] = useState([]);

    // ── additional tests approval ────────────────────────────────────────────
    const [additionalTests, setAdditionalTests] = useState([]);
    const [additionaltestapprove, setAdditionaltestapprove] = useState(false);

    // ── UI state ────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("summary");
    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [showSessionPopup, setShowSessionPopup] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileSizeErrorMsg, setFileSizeErrorMsg] = useState("");
    const [successPopup, setSuccessPopup] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [showImagePopup, setShowImagePopup] = useState(false);
    const [popupImageUrl, setPopupImageUrl] = useState(null);
    const [clinicalPopupList, setClinicalPopupList] = useState([]);
    const [clinicalPopupIndex, setClinicalPopupIndex] = useState(0);
    const [isClinicalLoading, setIsClinicalLoading] = useState(false);

    // ── payment form ────────────────────────────────────────────────────────
    const [paymentMode, setPaymentMode] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentId, setPaymentId] = useState("");
    const [isPaymentLoading, setIsPaymentLoading] = useState(false);

    // ── additional tests payment form ───────────────────────────────────────
    const [addlPaymentMode, setAddlPaymentMode] = useState("");
    const [addlPaymentDate, setAddlPaymentDate] = useState("");
    const [addlPaymentId, setAddlPaymentId] = useState("");
    const [isAddlPaymentLoading, setIsAddlPaymentLoading] = useState(false);

    // ── init ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!token) { navigate("/"); return; }
        if (window.site?.common?.imageCloudfront) {
            setImageCloudfront(window.site.common.imageCloudfront.replace(/^http:\/\//i, "https://"));
            setResourceBucket(window.site.common.resourceBucket);
        } else {
            setTimeout(() => {
                if (window.site?.common?.imageCloudfront) {
                    setImageCloudfront(window.site.common.imageCloudfront.replace(/^http:\/\//i, "https://"));
                    setResourceBucket(window.site.common.resourceBucket);
                }
            }, 1000);
        }
        fetchCase();
        fetchPathologists();
        fetchDermatologists();
        fetchLookupOptions("Biopsy");
    }, []);

    // ── pre-populate pathologist dropdown from loaded case ─────────────────
    useEffect(() => {
        if (caseDetails?.assignedPathologist) {
            setSelectedPathologistId(caseDetails.assignedPathologist);
        }
    }, [caseDetails?.assignedPathologist]);

    // ── pre-populate dermatologist dropdown from loaded case ────────────────
    useEffect(() => {
        if (caseDetails?.dermatologistId) {
            setSelectedDermatologist({ value: caseDetails.dermatologistId, label: caseDetails.dermatologistName || "" });
        }
    }, [caseDetails?.dermatologistId]);

    // ── pre-populate biopsy type dropdown from loaded case ──────────────────
    useEffect(() => {
        if (caseDetails?.biopsyType) {
            setSelectedBiopsyType(caseDetails.biopsyType);
        }
    }, [caseDetails?.biopsyType]);

    // ── pre-populate additional tests approval from loaded case ────────────
    useEffect(() => {
        if (caseDetails) {
            setAdditionalTests(caseDetails?.additionalTests || []);
            setAdditionaltestapprove(!!caseDetails?.additionaltestapprove);
        }
    }, [caseDetails]);

    // ── auto-update histology files in case once every upload has resolved ──
    useEffect(() => {
        if (
            histologyImageFiles.length > 0 &&
            uploadedHistologyFiles.length === histologyImageFiles.length &&
            uploadedHistologyFiles.every((f) => f.uploadStatus !== "INPROGRESS")
        ) {
            updateBiopsyCaseHistology();
        }
    }, [uploadedHistologyFiles]);

    // ── OSD viewer creation ─────────────────────────────────────────────────
    useEffect(() => {
        if (!isPopupOpen || !dziUrl || !viewerRef.current) return;

        const isDziURL = dziUrl.includes("output.dzi");
        const { width, height } = imageDimensions;
        const tileSource = isDziURL
            ? { Image: { xmlns: "http://schemas.microsoft.com/deepzoom/2008", Url: encodeURI(dziUrl), Format: "jpeg", Overlap: 2, TileSize: 256, Size: { Width: width, Height: height } } }
            : { type: "image", url: encodeURI(dziUrl), buildPyramid: false };

        const osdViewer = OpenSeadragon({
            id: "openseadragon-viewer",
            prefixUrl: "https://openseadragon.github.io/openseadragon/images/",
            tileSources: tileSource,
            showNavigator: true,
            crossOriginPolicy: "Anonymous",
            navigatorPosition: "BOTTOM_RIGHT",
            maxZoomPixelRatio: 10,
            minZoomImageRatio: 0.5,
            visibilityRatio: 1,
            zoomPerScroll: 2,
            homeFillsViewer: true,
            gestureSettingsMouse: { clickToZoom: true, dblClickToZoom: true, flickEnabled: true, pinchToZoom: true, scrollToZoom: true },
            showZoomControl: true,
            showHomeControl: true,
            showFullPageControl: true,
            showRotationControl: true,
            wrapHorizontal: false,
        });
        setViewer(osdViewer);
        return () => { if (osdViewer) osdViewer.destroy(); };
    }, [dziUrl, imageDimensions]);

    // ── keyboard navigation for DZI popup ──────────────────────────────────
    useEffect(() => {
        if (!isPopupOpen || dziImageList.length <= 1) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") navigateDziImage(dziCurrentIndex - 1);
            if (e.key === "ArrowRight") navigateDziImage(dziCurrentIndex + 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isPopupOpen, dziImageList, dziCurrentIndex, viewer]);

    // ── keyboard navigation for clinical popup ──────────────────────────────
    useEffect(() => {
        if (!showImagePopup || clinicalPopupList.length <= 1) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") navigateClinicalImage(clinicalPopupIndex - 1);
            if (e.key === "ArrowRight") navigateClinicalImage(clinicalPopupIndex + 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showImagePopup, clinicalPopupList, clinicalPopupIndex]);

    // ── cleanup polling on unmount ──────────────────────────────────────────
    useEffect(() => {
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    // ── data fetching ────────────────────────────────────────────────────────

    const fetchCase = () => {
        setIsLoading(true);
        axios.get(`${lambda}/biopsyCaseInfo?appname=${appname}&patientid=${id}&token=${token}`)
            .then((res) => {
                if (res?.data?.result === "Invalid token or Expired") {
                    setShowSessionPopup(true);
                } else if (res?.data?.result?.length > 0) {
                    setCaseDetails(res.data.result[0]);
                }
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    };

    const fetchCaseAsync = async () => {
        try {
            const res = await axios.get(`${lambda}/biopsyCaseInfo?appname=${appname}&patientid=${id}&token=${token}`);
            if (res?.data?.result?.length > 0) {
                setCaseDetails(res.data.result[0]);
            }
        } catch (err) {
            console.error("fetchCaseAsync error:", err);
        }
    };

    const fetchPathologists = async () => {
        try {
            const res = await axios.get(`${lambda}/userInfo?appname=${appname}&userType=PATHOLOGIST&token=${token}`);
            if (res?.data?.result && Array.isArray(res.data.result)) {
                setPathologistList(res.data.result);
            }
        } catch (err) {
            console.error("fetchPathologists error:", err);
        }
    };

    const fetchDermatologists = async () => {
        try {
            const [doctorRes, adminRes] = await Promise.all([
                axios.get(`${lambda}/userInfo?appname=${appname}&userType=DOCTOR&token=${token}`),
                axios.get(`${lambda}/userInfo?appname=${appname}&userType=ADMIN&token=${token}`),
            ]);
            const doctors = Array.isArray(doctorRes?.data?.result) ? doctorRes.data.result : [];
            const admins = Array.isArray(adminRes?.data?.result) ? adminRes.data.result : [];
            setDermatologistList([...doctors, ...admins]);
        } catch (err) {
            console.error("fetchDermatologists error:", err);
        }
    };

    const fetchLookupOptions = async (type) => {
        const res = await tmdbApi.lookupInfo(type);
        if (res?.result?.length > 0) {
            if (type === "Biopsy") setBiopsyTypeOptions(res.result);
        }
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setCaseDetails((prev) => ({ ...prev, [name]: value }));
    };

    const handleBiopsyTypeChange = (selected) => {
        setSelectedBiopsyType(selected);
        setCaseDetails((prev) => ({ ...prev, biopsyType: selected }));
    };

    const handleTestFieldChange = (e) => {
        const { name, value } = e.target;
        setCaseDetails((prev) => ({
            ...prev,
            defaultBiopsyTest: { ...(prev?.defaultBiopsyTest || {}), [name]: value },
        }));
    };

    const handleDeleteClinicalFile = async (fileId) => {
        GetTimeActivity();
        const updatedFiles = (caseDetails?.biopsyfiles || []).filter((f) => f.fileid !== fileId);
        try {
            await axios.post(
                `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
                { biopsyfiles: updatedFiles, updatedBy: labUserId },
                { headers: { "Content-Type": "application/json" } }
            );
            setCaseDetails((prev) => ({ ...prev, biopsyfiles: updatedFiles }));
            toast.success("File removed.");
        } catch (err) {
            console.error("Delete clinical file error:", err);
            toast.error("Failed to remove file.");
        }
    };

    const handleDeleteHistologyFile = async (fileId) => {
        GetTimeActivity();
        const updatedFiles = (caseDetails?.histologyImages || []).filter((f) => f.fileid !== fileId);
        try {
            await axios.post(
                `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
                { histologyImages: updatedFiles, updatedBy: labUserId },
                { headers: { "Content-Type": "application/json" } }
            );
            setCaseDetails((prev) => ({ ...prev, histologyImages: updatedFiles }));
            toast.success("File removed.");
        } catch (err) {
            console.error("Delete histology file error:", err);
            toast.error("Failed to remove file.");
        }
    };

    const handleHistologyImageRemove = (key) => {
        const name = histologyImageUrls?.[key]?.name;
        setHistologyImageFiles((prev) => prev.filter((f) => f.name !== name));
        setHistologyImageUrls((prev) => {
            if (!prev) return prev;
            const u = { ...prev };
            delete u[key];
            return u;
        });
    };

    // ── DZI polling ──────────────────────────────────────────────────────────

    const startDziPolling = () => {
        if (intervalRef.current) return;
        setIsDziProcessing(true);
        intervalRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${lambda}/biopsyCaseInfo?appname=${appname}&patientid=${id}&token=${token}`);
                if (res?.data?.result?.[0]) {
                    const updated = res.data.result[0];
                    setCaseDetails(updated);
                    const pending = (updated.histologyImages || []).filter(
                        (f) => f.allow && (!f.dzistatus || f.dzistatus.toUpperCase() !== "COMPLETED")
                    );
                    if (pending.length === 0) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                        setIsDziProcessing(false);
                    }
                }
            } catch { /* ignore poll errors */ }
        }, 10000);
    };

    // ── histology upload ─────────────────────────────────────────────────────

    const handleHistologyFileChange = async (e, typeofFile) => {
        const selectedFiles = Array.from(e.target.files);
        e.target.value = "";

        for (const file of selectedFiles) {
            try {
                const dims = await getFileDimensions(file);
                if (dims.width * dims.height > MAX_PIXELS) {
                    setFileSizeError(true);
                    setFileSizeErrorMsg(`"${file.name}" resolution exceeds 10 billion pixels.`);
                    continue;
                }
                setFileDimension((prev) => ({ ...prev, [file.name]: dims }));

                const urlobj = URL.createObjectURL(file);
                const adjustedIndex = histologyImageIndex;
                setHistologyImageFiles((prev) => [...prev, file]);
                setHistologyImageUrls((prev) => ({
                    ...prev,
                    [`blob${adjustedIndex}`]: { url: urlobj, name: file.name },
                }));
                setHistologyImageIndex((prev) => prev + 1);
            } catch (err) {
                console.error("Dimension read error:", err);
            }
        }
    };

    const startHistologyUploads = () => {
        const bucket = resourceBucket || window.site?.common?.resourceBucket;

        for (const file of histologyImageFiles) {
            const timestamp = Date.now();
            const ObjectID = require("bson-objectid");
            const uniqueId = new ObjectID().toString();
            const fileExtension = file.name.split(".").pop();
            const fileNameUnique = `${uniqueId}_image_${timestamp}.${fileExtension}`;
            const isTiff = file.type?.toLowerCase().includes("tiff") || file.type?.toLowerCase().includes("tif");
            const isLarge = file.size > 8 * 1024 * 1024;

            const uploadFilePath = (isTiff || isLarge)
                ? `${appname}/cases/histology/${id}/${uniqueId}/`
                : `${appname}/cases/histology-jpg/${id}/${uniqueId}/`;

            let newFile = {
                fileCreated: new Date(),
                filename: fileNameUnique,
                filetype: file.type,
                sourcepath: uploadFilePath + fileNameUnique,
                fileid: uniqueId,
                originalFilename: file.name,
                uploadStatus: "INPROGRESS",
                fileSize: file.size,
                width: fileDimension?.[file.name]?.width || 0,
                height: fileDimension?.[file.name]?.height || 0,
                resolution: (fileDimension?.[file.name]?.width || 0) * (fileDimension?.[file.name]?.height || 0),
            };
            if (isTiff || isLarge) {
                newFile.allow = true;
                newFile.allowDziCreation = true;
            }
            if (!isLarge && (file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png")) {
                newFile.dzistatus = "COMPLETED";
            }

            const uploaderOptions = {
                file,
                baseURL: UPLOADER_BASE_URL,
                chunkSize: 100,
                threadsQuantity: 3,
                useTransferAcceleration: undefined,
                bucket: uploadFilePath,
                fileNameUnique,
                sourceBucket: bucket,
                setIsUploading,
                setIsUploadingFailed,
            };

            const uploader = new Uploader(uploaderOptions);
            uploader.onComplete(() => {
                setUploadedHistologyFiles((prev) =>
                    prev.map((f) => (f.fileid === newFile.fileid ? { ...f, uploadStatus: "COMPLETED" } : f))
                );
            });
            uploader.onError(() => {
                setUploadedHistologyFiles((prev) =>
                    prev.map((f) => (f.fileid === newFile.fileid ? { ...f, uploadStatus: "FAILED" } : f))
                );
            });
            setUploadedHistologyFiles((prev) => [...prev, newFile]);
            uploader.start();
        }
    };

    const updateBiopsyCaseHistology = () => {
        const mergedHistologyImages = [...(caseDetails?.histologyImages || []), ...uploadedHistologyFiles];
        axios.post(
            `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
            { histologyImages: mergedHistologyImages, updatedBy: labUserId },
            { headers: { "Content-Type": "application/json" } }
        ).then(() => {
            setHistologyImageFiles([]);
            setHistologyImageUrls(null);
            setHistologyImageIndex(0);
            setUploadedHistologyFiles([]);
            fetchCase();
            startDziPolling();
        }).catch((err) => console.error("updateBiopsyCaseHistology error:", err));
    };

    // ── clinical photo upload ────────────────────────────────────────────────

    const handleClinicalImageSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const maxSize = 1048612 * 20;
        selectedFiles.forEach((file) => {
            if (file.size >= maxSize) {
                setFileSizeError(true);
                setFileSizeErrorMsg("File size exceeds 20MB limit.");
                return;
            }
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                const key = `${file.name}-${Date.now()}-${Math.random()}`;
                setClinicalImageFiles((prev) => [...prev, { id: key, file }]);
                setClinicalImageUrls((prev) => ({ ...prev, [key]: { url, name: file.name, id: key } }));
            };
            img.src = url;
        });
    };

    const handleClinicalImageRemove = (key) => {
        setClinicalImageFiles((prev) => prev.filter((f) => f.id !== key));
        setClinicalImageUrls((prev) => { const u = { ...prev }; delete u[key]; return u; });
    };

    const uploadClinicalPhotos = async () => {
        const bucket = window.site?.common?.resourceBucket || resourceBucket;
        const filesArray = [];

        await Promise.all(
            clinicalImageFiles.map(async ({ file, id: fileKey }) => {
                const timestamp = Date.now();
                const ObjectID = require("bson-objectid");
                const uniqueId = new ObjectID().toString();
                const ext = file.name.split(".").pop();
                const fileNameUnique = `${uniqueId}_image_${timestamp}.${ext}`;
                const uploadPath = `${appname}/${id}/${uniqueId}/${fileNameUnique}`;

                const newFile = {
                    filename: file.name.replace(/ /g, "_").split(".")[0],
                    sourcepath: uploadPath,
                    fileid: uniqueId,
                    filetype: file.type,
                    filesize: file.size,
                    fileformat: ext,
                };
                filesArray.push(newFile);

                try {
                    const signedRes = await axios.post(
                        `${lambda}/uploadFiles?appname=${appname}&biopsyid=${id}`,
                        { source_bucket: bucket, sourcepath: uploadPath },
                        { headers: { "Content-Type": "application/json" } }
                    );
                    if (signedRes?.data?.result) {
                        await axios.put(signedRes.data.result, file, {
                            headers: { "Content-Type": file.type, "Cache-Control": "no-cache" },
                        });
                    }
                } catch (err) {
                    console.error("Clinical photo S3 upload error:", err);
                }
            })
        );

        await axios.post(
            `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
            { biopsyfiles: filesArray, updatedBy: labUserId },
            { headers: { "Content-Type": "application/json" } }
        );

        setClinicalImageFiles([]);
        setClinicalImageUrls(null);
        await fetchCaseAsync();
    };

    // ── pathologist assignment ────────────────────────────────────────────────

    const assignPathologist = async () => {
        const selected = pathologistList.find((p) => p.userid === selectedPathologistId);
        await axios.post(
            `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
            {
                assignedPathologist: selectedPathologistId,
                assignedPathologistName: selected?.name || "",
                status: "Assigned to Pathologist",
                updatedBy: labUserId,
                labInchargeid:labUserId
            },
            { headers: { "Content-Type": "application/json" } }
        );
        setCaseDetails((prev) => ({
            ...prev,
            status: "Assigned to Pathologist",
            assignedPathologist: selectedPathologistId,
            assignedPathologistName: selected?.name || "",
        }));
    };

    // ── save the editable patient-summary fields ────────────────────────────

    const updateCaseFields = async () => {
        const payload = {
            clinicregistrationnumber: caseDetails?.clinicregistrationnumber,
            patientname: caseDetails?.patientname,
            age: caseDetails?.age,
            gender: caseDetails?.gender,
            specimentype: caseDetails?.specimentype,
            siteofbiopsy: caseDetails?.siteofbiopsy,
            clinicalhistory: caseDetails?.clinicalhistory,
            biopsyType: selectedBiopsyType,
            defaultBiopsyTest: {
                name: caseDetails?.defaultBiopsyTest?.name,
                cost: caseDetails?.defaultBiopsyTest?.cost,
                isDefault: true,
            },
            updatedBy: labUserId,
        };
        if (selectedDermatologist) {
            payload.dermatologistId = selectedDermatologist.value;
            payload.dermatologistName = selectedDermatologist.label;
        }
        if (additionaltestapprove && caseDetails?.additionalPaymentInfo?.paymentId) {
            payload.status = "Reassigned to Pathologist";
        }
        await axios.post(
            `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
            payload,
            { headers: { "Content-Type": "application/json" } }
        );
        if (payload.status) {
            setCaseDetails((prev) => ({ ...prev, status: payload.status }));
        }
    };

    // ── unified update (field edits + clinical upload + histology upload + assign pathologist) ──

    const handleUpdate = async () => {
        GetTimeActivity();
        const hasClinical = clinicalImageFiles.length > 0;
        const hasHistology = histologyImageFiles.length > 0;
        const hasPathologist = !!selectedPathologistId && selectedPathologistId !== caseDetails?.assignedPathologist;

        setIsActionLoading(true);
        try {
            await updateCaseFields();
            if (hasClinical) {
                await uploadClinicalPhotos();
            }
            if (hasHistology) {
                startHistologyUploads();
            }
            if (hasPathologist) {
                await assignPathologist();
            }
            setSuccessMsg("Updated successfully.");
            setSuccessPopup(true);
        } catch (err) {
            console.error("handleUpdate error:", err);
            toast.error("Update failed. Please try again.");
        } finally {
            setIsActionLoading(false);
        }
    };

    // ── payment confirmation ──────────────────────────────────────────────────

    const handleConfirmPayment = async () => {
        GetTimeActivity();
        if (!paymentMode) { toast.error("Please select a payment mode."); return; }
        if (!paymentDate) { toast.error("Please select a payment date."); return; }
        if (!paymentId.trim()) { toast.error("Please enter a payment / transaction ID."); return; }

        setIsPaymentLoading(true);
        try {
            const paymentInfo = {
                testName: caseDetails?.defaultBiopsyTest?.name || "Biopsy",
                amount: caseDetails?.defaultBiopsyTest?.cost || "1000",
                paymentMode,
                paymentDate,
                paymentId: paymentId.trim(),
                confirmedAt: new Date().toISOString(),
                confirmedBy: labUserId,
            };
            await axios.post(
                `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
                { paymentInfo, status: "Payment Received", updatedBy: labUserId },
                { headers: { "Content-Type": "application/json" } }
            );
            setCaseDetails((prev) => ({ ...prev, paymentInfo, status: "Payment Received" }));
            toast.success("Payment confirmed successfully.");
        } catch (err) {
            console.error("Confirm payment error:", err);
            toast.error("Failed to confirm payment. Please try again.");
        } finally {
            setIsPaymentLoading(false);
        }
    };

    const handleConfirmAdditionalPayment = async () => {
        GetTimeActivity();
        if (!addlPaymentMode) { toast.error("Please select a payment mode."); return; }
        if (!addlPaymentDate) { toast.error("Please select a payment date."); return; }
        if (!addlPaymentId.trim()) { toast.error("Please enter a payment / transaction ID."); return; }

        setIsAddlPaymentLoading(true);
        try {
            const additionalTestsTotal = additionalTests.reduce((sum, t) => sum + (parseFloat(t.fee) || 0), 0);
            const additionalPaymentInfo = {
                testName: additionalTests.map((t) => t.name).join(", "),
                amount: additionalTestsTotal,
                paymentMode: addlPaymentMode,
                paymentDate: addlPaymentDate,
                paymentId: addlPaymentId.trim(),
                confirmedAt: new Date().toISOString(),
                confirmedBy: labUserId,
            };
            await axios.post(
                `${lambda}/updateBiopsyCase?appname=${appname}&patientid=${id}&token=${token}`,
                {
                    additionalPaymentInfo,
                    additionalTests,
                    additionalTestsTotal,
                    additionaltestapprove,
                    updatedBy: labUserId,
                },
                { headers: { "Content-Type": "application/json" } }
            );
            setCaseDetails((prev) => ({ ...prev, additionalPaymentInfo }));
            toast.success("Additional tests payment confirmed successfully.");
        } catch (err) {
            console.error("Confirm additional payment error:", err);
            toast.error("Failed to confirm payment. Please try again.");
        } finally {
            setIsAddlPaymentLoading(false);
        }
    };

    // ── DZI helpers ──────────────────────────────────────────────────────────

    const buildTileSourceForFile = (file) => {
        let path = file.dziUrl || "";
        if (path.startsWith("/")) path = path.slice(1);
        const isDziURL = path.includes("output.dzi");
        if (isDziURL) {
            return {
                Image: {
                    xmlns: "http://schemas.microsoft.com/deepzoom/2008",
                    Url: encodeURI(imageCloudfront + path + "_files/"),
                    Format: "jpeg",
                    Overlap: 2,
                    TileSize: 256,
                    Size: { Width: file.width, Height: file.height },
                },
            };
        }
        return {
            type: "image",
            url: encodeURI(`${imageCloudfront}${(file.sourcepath || "").replace(/ /g, "%20")}?auto=compress,format`),
            buildPyramid: false,
        };
    };

    const openDziPopup = (file, filesList) => {
        setPopupOpen(true);
        setImageDimensions({ width: file.width, height: file.height });
        let path = file.dziUrl || "";
        if (path.startsWith("/")) path = path.slice(1);
        const isDziURL = path.includes("output.dzi");
        if (isDziURL) {
            setDziUrl(imageCloudfront + path + "_files/");
        } else {
            setDziUrl(`${imageCloudfront}${(file.sourcepath || "").replace(/ /g, "%20")}?auto=compress,format`);
        }
        setDziUrlFile(file.originalFilename || file.filename);
        setDZIPopupImage(imageCloudfront + file.sourcepath);
        setPopupLoading(true);
        if (filesList?.length) {
            setDziImageList(filesList);
            setDziCurrentIndex(filesList.findIndex((f) => f.fileid === file.fileid));
        }
    };

    const navigateDziImage = (newIndex) => {
        if (!dziImageList.length) return;
        const clamped = Math.max(0, Math.min(newIndex, dziImageList.length - 1));
        const file = dziImageList[clamped];
        if (!file) return;
        const tileSource = buildTileSourceForFile(file);
        if (viewer) {
            setIsDziLoading(true);
            viewer.open(tileSource);
            viewer.viewport.setRotation(0);
            viewer.addOnceHandler("open", () => setIsDziLoading(false));
        }
        setDziCurrentIndex(clamped);
        setDziUrlFile(file.originalFilename || file.filename);
        setDZIPopupImage(imageCloudfront + file.sourcepath);
        setRotation(0);
    };

    const closeDeepZoom = () => {
        setPopupOpen(false);
        setDziUrl("");
        setDziUrlFile("");
        setImageDimensions({ width: 0, height: 0 });
        setDziImageList([]);
        setDziCurrentIndex(0);
        if (viewer) { viewer.destroy(); setViewer(null); }
    };

    const navigateClinicalImage = (newIndex) => {
        if (!clinicalPopupList.length) return;
        const clamped = Math.max(0, Math.min(newIndex, clinicalPopupList.length - 1));
        setIsClinicalLoading(true);
        setPopupImageUrl(clinicalPopupList[clamped]);
        setClinicalPopupIndex(clamped);
    };

    const openClinicalPopup = (list, index) => {
        setShowImagePopup(true);
        setIsClinicalLoading(true);
        setPopupImageUrl(list[index]);
        setClinicalPopupList(list);
        setClinicalPopupIndex(index);
    };

    // ── render helpers ───────────────────────────────────────────────────────

    const readOnlyField = (label, value) => (
        <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="mb-3 input-field">
                <label className="form_label">{label}</label>
                <input type="text" className="form-control" disabled value={value || ""} />
            </div>
        </div>
    );

    const renderStatusBadge = (status) => {
        if (!status) return null;
        const style = STATUS_COLORS[status] || { background: "#6c757d" };
        return (
            <span className="ms-3" style={{ ...style, color: style.color || "#fff", fontSize: "12px", fontWeight: "600", padding: "3px 10px", borderRadius: "4px" }}>
                {status}
            </span>
        );
    };

    // ── render ───────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div id="layout-wrapper">
                <Header /><SideMenu />
                <div className="main-content" id="main-content">
                    <div className="page-content"><Loader /></div>
                </div>
            </div>
        );
    }

    return (
        <>
            {showSessionPopup && <SessionPopup />}

            <SweetAlert
                show={successPopup}
                custom
                confirmBtnText="Ok"
                confirmBtnBsStyle="primary"
                title={successMsg}
                onConfirm={() => setSuccessPopup(false)}
            />

            <div id="layout-wrapper">
                <Header />
                <SideMenu />

                <div className="main-content user-management create-user" id="main-content">
                    <div className="page-content">
                        <div className="container-fluid">

                            {/* ── breadcrumb ─────────────────────────────────── */}
                            <div className="row mb-4 breadcrumb">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-2 card-title">
                                                Biopsy Case
                                                {caseDetails?.pathrefno && <span className="ms-2 text-muted" style={{ fontSize: "14px" }}>#{caseDetails.pathrefno}</span>}
                                                {renderStatusBadge(caseDetails?.status)}
                                            </h4>
                                        </div>
                                        <a onClick={() => { GetTimeActivity(); navigate("/biopsy/cases"); }} className="create-quiz-btn border-0" style={{ cursor: "pointer" }}>Back</a>
                                    </div>
                                </div>
                            </div>

                            {/* ── tabs ───────────────────────────────────────── */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body p-0">
                                            <ul className="nav nav-tabs" style={{ borderBottom: "1px solid #dee2e6", padding: "0 16px" }}>
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link ${activeTab === "summary" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("summary")}
                                                    >
                                                        Patient Summary
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link ${activeTab === "payment" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("payment")}
                                                    >
                                                        Payment
                                                    </button>
                                                </li>
                                            </ul>

                                            <div className="p-4">

                                                {/* ════ PATIENT SUMMARY TAB ════ */}
                                                {activeTab === "summary" && (
                                                    <div>

                                                        {/* ── form fields (Path Ref No / Biopsy Date stay read-only) ── */}
                                                        <div className="row col-lg-12">
                                                            {readOnlyField("PATH REF NO", caseDetails?.pathrefno)}
                                                            {readOnlyField("BIOPSY DATE", caseDetails?.biopsydate)}

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">CLINIC REGISTRATION NUMBER</label>
                                                                    <input
                                                                        name="clinicregistrationnumber"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.clinicregistrationnumber || ""}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">PATIENT NAME</label>
                                                                    <input
                                                                        name="patientname"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.patientname || ""}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">AGE</label>
                                                                    <input
                                                                        name="age"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.age || ""}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">GENDER</label>
                                                                    <select
                                                                        name="gender"
                                                                        className="form-control form-select"
                                                                        value={caseDetails?.gender || ""}
                                                                        onChange={handleFieldChange}
                                                                    >
                                                                        <option value="">Select</option>
                                                                        <option value="Male">Male</option>
                                                                        <option value="Female">Female</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">SPECIMEN TYPE</label>
                                                                    <input
                                                                        name="specimentype"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.specimentype || ""}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">SITE OF BIOPSY</label>
                                                                    <input
                                                                        name="siteofbiopsy"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.siteofbiopsy || ""}
                                                                        onChange={handleFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">BIOPSY TYPE</label>
                                                                    <Select
                                                                        isMulti={false}
                                                                        placeholder="Select Biopsy Type"
                                                                        value={selectedBiopsyType}
                                                                        onChange={handleBiopsyTypeChange}
                                                                        options={biopsyTypeOptions.map((item) => ({
                                                                            value: item.lookupcode,
                                                                            label: item.name,
                                                                        }))}
                                                                        isClearable
                                                                    />
                                                                </div>
                                                            </div>

                                                            {!dermatologistList.some((d) => d.userid === caseDetails?.createdby) && (
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">SELECT DERMATOLOGIST</label>
                                                                        <Select
                                                                            isMulti={false}
                                                                            placeholder="Select Dermatologist"
                                                                            value={selectedDermatologist}
                                                                            onChange={(selected) => setSelectedDermatologist(selected)}
                                                                            options={dermatologistList.map((d) => ({
                                                                                value: d.userid,
                                                                                label: d.name,
                                                                            }))}
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">TEST NAME</label>
                                                                    <input
                                                                        name="name"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.defaultBiopsyTest?.name || ""}
                                                                        onChange={handleTestFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">COST (₹)</label>
                                                                    <input
                                                                        name="cost"
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={caseDetails?.defaultBiopsyTest?.cost || ""}
                                                                        onChange={handleTestFieldChange}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Clinical History textarea */}
                                                            <div className="col-lg-12 col-md-12 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <label className="form_label">CLINICAL HISTORY</label>
                                                                    <textarea
                                                                        name="clinicalhistory"
                                                                        className="form-control edit-cpc-textarea"
                                                                        rows={6}
                                                                        value={caseDetails?.clinicalhistory || ""}
                                                                        onChange={handleFieldChange}
                                                                        style={{ height: "150px" }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* ══════════════════════════════════════ */}
                                                        {/* ── 1. CLINICAL HISTORY PHOTOS ─────── */}
                                                        {/* ══════════════════════════════════════ */}
                                                        <div className="row col-lg-12 mt-4 upload-imgs">
                                                            <div className="col-lg-12 align-items-start justify-content-between upload-img-section pe-0 me-0">
                                                                <label className="form_label col-12">CLINICAL HISTORY PHOTOS</label>
                                                            </div>
                                                            <div className="mt-2 mb-0 pe-0">
                                                                <p className="mt-0 mb-0 text-muted file-format">Note: Max File size: 20 MB | Formats: JPG, PNG</p>
                                                                {fileSizeError && <p className="text-danger small mt-1">{fileSizeErrorMsg}</p>}

                                                                <div className="d-flex justify-content-start col-lg-12 clinical-photo-section gap-3 mt-3">
                                                                    <div className="uploaded_images-grid mb-3 mt-2">

                                                                        {/* ADD box */}
                                                                        <div className="upload-box">
                                                                            <div className="file_upload add-box">
                                                                                <input
                                                                                    type="file"
                                                                                    id="clinical-image-upload"
                                                                                    accept="image/jpeg,image/png"
                                                                                    multiple
                                                                                    className="file-input"
                                                                                    onChange={handleClinicalImageSelect}
                                                                                    style={{ display: "none" }}
                                                                                />
                                                                                <label htmlFor="clinical-image-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                    <p className="mb-0"><span className="material-symbols-outlined plus-icon">add</span></p>
                                                                                    ADD
                                                                                </label>
                                                                            </div>
                                                                        </div>

                                                                        {/* newly selected (blob preview) */}
                                                                        {clinicalImageUrls && Object.keys(clinicalImageUrls).map((key) => (
                                                                            <div className="thumb-box" key={key}>
                                                                                <div className="thumb-img cursor-pointer" onClick={() => {
                                                                                    const blobUrls = Object.keys(clinicalImageUrls).map((k) => clinicalImageUrls[k].url);
                                                                                    const serverUrls = (caseDetails?.biopsyfiles || []).map((f) => `${imageCloudfront}${f.sourcepath?.replace(/ /g, "%20")}?auto=compress,format`);
                                                                                    openClinicalPopup([...blobUrls, ...serverUrls], Object.keys(clinicalImageUrls).indexOf(key));
                                                                                }}>
                                                                                    <img src={clinicalImageUrls[key].url} alt="clinical" />
                                                                                    <span className="icon-close" onClick={(e) => { e.stopPropagation(); handleClinicalImageRemove(key); }}>×</span>
                                                                                </div>
                                                                                <p className="thumb-name text-truncate small mb-0">{clinicalImageUrls[key].name}</p>
                                                                            </div>
                                                                        ))}

                                                                        {/* existing saved photos */}
                                                                        {caseDetails?.biopsyfiles?.map((item, key) => {
                                                                            const imgPath = `${imageCloudfront}${item.sourcepath?.replace(/ /g, "%20")}?auto=compress,format`;
                                                                            return (
                                                                                <div className="thumb-box" key={key}>
                                                                                    <div className="thumb-img cursor-pointer" onClick={() => {
                                                                                        const blobUrls = clinicalImageUrls ? Object.keys(clinicalImageUrls).map((k) => clinicalImageUrls[k].url) : [];
                                                                                        const serverUrls = (caseDetails.biopsyfiles || []).map((f) => `${imageCloudfront}${f.sourcepath?.replace(/ /g, "%20")}?auto=compress,format`);
                                                                                        openClinicalPopup([...blobUrls, ...serverUrls], blobUrls.length + key);
                                                                                    }}>
                                                                                        <img
                                                                                            src={`${imgPath}&width=150`}
                                                                                            alt="clinical"
                                                                                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = deepZoom; }}
                                                                                        />
                                                                                        <span className="icon-close" onClick={(e) => { e.stopPropagation(); handleDeleteClinicalFile(item.fileid); }}>×</span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>

                                                        {/* ══════════════════════════════════════ */}
                                                        {/* ── 2. HISTOLOGY PHOTOS ────────────── */}
                                                        {/* ══════════════════════════════════════ */}
                                                        <div className="row col-lg-12 mt-4 upload-imgs">
                                                            <div className="col-lg-12 align-items-start justify-content-between upload-img-section pe-0 me-0">
                                                                <label className="form_label col-12">HISTOLOGY PHOTOS</label>
                                                            </div>

                                                            {/* JPEG section */}
                                                            <div className="mt-2 mb-0 pe-0">
                                                                <div className="d-flex justify-content-between histology-photo-section">
                                                                    <p className="case-type-text">PNG, JPEG, JPG Files Only</p>
                                                                    <p className="mt-0 mb-0 text-muted file-format">Note: Max File size: 20 MB, Max files: 20 | Formats: PNG, JPEG, JPG</p>
                                                                </div>
                                                                <div className="d-flex justify-content-start col-lg-12 histology-photo-section gap-2">
                                                                    <div className="mb-1 mt-2 input-field deep-zoom-styles">
                                                                        <div className="upload-image">
                                                                            <div className="file_upload add-box">
                                                                                <input
                                                                                    type="file"
                                                                                    id="histology-jpeg-upload"
                                                                                    accept=".jpeg,.jpg,.png"
                                                                                    multiple
                                                                                    className="file-input"
                                                                                    onChange={(e) => handleHistologyFileChange(e, "histologyJpeg")}
                                                                                    style={{ display: "none" }}
                                                                                />
                                                                                <label htmlFor="histology-jpeg-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                    <p className="mb-0"><span className="material-symbols-outlined plus-icon">add</span></p>
                                                                                    ADD
                                                                                </label>
                                                                            </div>
                                                                        </div>

                                                                        {/* newly selected JPEG blobs — show deepZoom placeholder + delete */}
                                                                        {histologyImageUrls && Object.keys(histologyImageUrls)
                                                                            .filter((k) => getExtension(histologyImageUrls[k].name) === ".jpeg,.jpg,.png")
                                                                            .map((key) => (
                                                                                <div className="thumb-box" key={key}>
                                                                                    <div className="thumb-img">
                                                                                        <img src={deepZoom} alt="histology" />
                                                                                        <span className="icon-close" onClick={() => handleHistologyImageRemove(key)}>×</span>
                                                                                    </div>
                                                                                    <p className="thumb-name text-truncate small mb-0">{histologyImageUrls[key].name}</p>
                                                                                </div>
                                                                            ))}

                                                                        {/* saved JPEG histology images */}
                                                                        {caseDetails?.histologyImages?.filter((f) => getExtension(f.originalFilename || f.filename) === ".jpeg,.jpg,.png")
                                                                            .map((file, key) => {
                                                                                const imgPath = `${imageCloudfront}${(file.sourcepath || "").replace(/ /g, "%20")}?auto=compress,format`;
                                                                                const showFailed = file.uploadStatus === "FAILED" || isStaleUpload(file) || checkStuckUploads(file);
                                                                                return (
                                                                                    <div className="thumb-box" key={key}>
                                                                                        <div
                                                                                            className="thumb-img cursor-pointer"
                                                                                            onClick={() => {
                                                                                                if (file.allow && (!file.dzistatus || file.dzistatus.toUpperCase() !== "COMPLETED")) return;
                                                                                                const viewable = (caseDetails.histologyImages || []).filter(
                                                                                                    (f) => f.uploadStatus === "COMPLETED" && getExtension(f.originalFilename || f.filename) === ".jpeg,.jpg,.png" && (!f.allow || f.dzistatus?.toUpperCase() === "COMPLETED")
                                                                                                );
                                                                                                openDziPopup(file, viewable);
                                                                                            }}
                                                                                        >
                                                                                            <img
                                                                                                src={file.thumbnailUrl ? `${imageCloudfront.replace(/\/$/, "")}${file.thumbnailUrl}?auto=compress,format&width=150` : imgPath}
                                                                                                alt={file.originalFilename}
                                                                                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = deepZoom; }}
                                                                                            />
                                                                                            <span className="icon-close" onClick={(e) => { e.stopPropagation(); handleDeleteHistologyFile(file.fileid); }}>×</span>
                                                                                            {showFailed && (
                                                                                                <span className="material-symbols-outlined img_view errorIcon">
                                                                                                    <img src={errorSvg} alt="Failed" />
                                                                                                </span>
                                                                                            )}
                                                                                            {file.uploadStatus === "INPROGRESS" && !showFailed && (
                                                                                                <span className="material-symbols-outlined img_view loadingIcon">
                                                                                                    <img src={loadingIcon} alt="Uploading" />
                                                                                                </span>
                                                                                            )}
                                                                                            {file.uploadStatus === "COMPLETED" && (file.dzistatus === "INPROGRESS" || !file.dzistatus) && file.allow && (
                                                                                                <span className="material-symbols-outlined img_view loadingIcon">
                                                                                                    <img src={progessIcon} alt="Processing" />
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* TIFF section */}
                                                            <div className="row col-lg-12 mt-0 upload-imgs">
                                                                <div className="d-flex justify-content-between tiff-files-section">
                                                                    <p className="case-type-text">TIFF Files</p>
                                                                    <p className="mt-0 mb-0 text-muted file-format">Note: Max File size: 2GB, Max files: 10 | Formats: TIF, TIFF</p>
                                                                </div>
                                                                <div className="d-flex justify-content-start col-lg-12 histology-photo-section gap-2">
                                                                    <div className="mb-3 mt-2 input-field deep-zoom-styles">
                                                                        <div className="upload-image">
                                                                            <div className="file_upload add-box">
                                                                                <input
                                                                                    type="file"
                                                                                    id="histology-tiff-upload"
                                                                                    accept=".tif,.tiff"
                                                                                    multiple
                                                                                    className="file-input"
                                                                                    onChange={(e) => handleHistologyFileChange(e, "histologyTiff")}
                                                                                    style={{ display: "none" }}
                                                                                />
                                                                                <label htmlFor="histology-tiff-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                    <p className="mb-0"><span className="material-symbols-outlined plus-icon">add</span></p>
                                                                                    ADD
                                                                                </label>
                                                                            </div>
                                                                        </div>

                                                                        {/* newly selected TIFF blobs — show deepZoom placeholder + delete */}
                                                                        {histologyImageUrls && Object.keys(histologyImageUrls)
                                                                            .filter((k) => getExtension(histologyImageUrls[k].name) === ".tif,.tiff")
                                                                            .map((key) => (
                                                                                <div className="upload_images d-flex gap-2 align-items-start upload-static-img" key={key}>
                                                                                    <div className="img" style={{ position: "relative" }}>
                                                                                        <span className="default-img default-static-img"><img src={deepZoom} alt="" /></span>
                                                                                        <span className="icon-close" onClick={() => handleHistologyImageRemove(key)} style={{ position: "absolute", top: 0, right: 0, cursor: "pointer", background: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "50%", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", lineHeight: 1 }}>×</span>
                                                                                    </div>
                                                                                </div>
                                                                            ))}

                                                                        {/* saved TIFF histology images */}
                                                                        {caseDetails?.histologyImages?.filter((f) => getExtension(f.originalFilename || f.filename) === ".tif,.tiff")
                                                                            .map((file, key) => {
                                                                                const showFailed = file.uploadStatus === "FAILED" || isStaleUpload(file) || checkStuckUploads(file);
                                                                                return (
                                                                                    <div className={`upload_images d-flex gap-2 align-items-start ${!file.thumbnailUrl ? "upload-static-img" : ""}`} key={key}>
                                                                                        <div
                                                                                            className="img cursor-pointer"
                                                                                            style={{ position: "relative" }}
                                                                                            onClick={() => {
                                                                                                if (file.dzistatus === "COMPLETED") {
                                                                                                    const viewable = (caseDetails.histologyImages || []).filter(
                                                                                                        (f) => f.uploadStatus === "COMPLETED" && getExtension(f.originalFilename || f.filename) === ".tif,.tiff" && f.dzistatus?.toUpperCase() === "COMPLETED"
                                                                                                    );
                                                                                                    openDziPopup(file, viewable);
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <span className={`default-img ${!file.thumbnailUrl ? "default-static-img" : ""}`}>
                                                                                                <img
                                                                                                    src={file.thumbnailUrl ? `${imageCloudfront.replace(/\/$/, "")}${file.thumbnailUrl}?auto=compress,format&width=150` : deepZoom}
                                                                                                    alt={file.originalFilename}
                                                                                                />
                                                                                            </span>
                                                                                            <span className="icon-close" onClick={(e) => { e.stopPropagation(); handleDeleteHistologyFile(file.fileid); }} style={{ position: "absolute", top: 0, right: 0, cursor: "pointer", background: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "50%", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", lineHeight: 1 }}>×</span>
                                                                                            {showFailed && (
                                                                                                <span className="material-symbols-outlined img_view errorIcon">
                                                                                                    <img src={errorSvg} alt="Failed" />
                                                                                                </span>
                                                                                            )}
                                                                                            {file.uploadStatus === "INPROGRESS" && !showFailed && (
                                                                                                <span className="material-symbols-outlined img_view loadingIcon">
                                                                                                    <img src={loadingIcon} alt="Uploading" />
                                                                                                </span>
                                                                                            )}
                                                                                            {file.uploadStatus === "COMPLETED" && (file.dzistatus === "INPROGRESS" || !file.dzistatus) && (
                                                                                                <span className="material-symbols-outlined img_view loadingIcon">
                                                                                                    <img src={progessIcon} alt="Processing" />
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </div>
                                                            </div>


                                                            {isDziProcessing && (
                                                                <p className="text-muted small mt-2">
                                                                    <span className="material-symbols-outlined" style={{ fontSize: "14px", verticalAlign: "middle" }}>hourglass_top</span>
                                                                    {" "}DZI processing in progress…
                                                                </p>
                                                            )}
                                                        </div>

                                                        {/* ══════════════════════════════════════ */}
                                                        {/* ── 3. ASSIGN TO PATHOLOGIST ──────── */}
                                                        {/* ══════════════════════════════════════ */}
                                                        <div className="row col-lg-12 mt-4">
                                                            <div className="col-lg-12">
                                                                <label className="form_label">ASSIGN TO PATHOLOGIST</label>
                                                            </div>
                                                            <div className="col-lg-4 col-md-6 col-sm-12">
                                                                <div className="mb-3 input-field">
                                                                    <select
                                                                        className="form-control form-select"
                                                                        value={selectedPathologistId}
                                                                        onChange={(e) => setSelectedPathologistId(e.target.value)}
                                                                    >
                                                                        <option value="">Select Pathologist</option>
                                                                        {pathologistList.map((p) => (
                                                                            <option key={p.userid} value={p.userid}>
                                                                                {p.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            {caseDetails?.assignedPathologistName && (
                                                                <div className="col-lg-12 mt-1">
                                                                    <p className="text-muted small mb-0">
                                                                        Currently assigned: <strong>{caseDetails.assignedPathologistName}</strong>
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* ── Update button (saves clinical photos + histology + pathologist) ── */}
                                                        <div className="col-md-12 mt-4">
                                                            <button
                                                                className="create-quiz-btn border-0"
                                                                onClick={handleUpdate}
                                                                disabled={isActionLoading}
                                                            >
                                                                {isActionLoading ? "Updating…" : "Update"}
                                                            </button>
                                                        </div>

                                                    </div>
                                                )}
                                                {/* ── end summary tab ────── */}

                                                {/* ════ PAYMENT TAB ════ */}
                                                {activeTab === "payment" && (
                                                    <div>
                                                        {caseDetails?.paymentInfo?.paymentId ? (
                                                            /* ── Payment confirmed — show summary table ── */
                                                            <div>
                                                                <div className="d-flex align-items-center gap-2 mb-4">
                                                                    <h5 className="mb-0" style={{ fontWeight: "600", color: "#333" }}>Payment Summary</h5>
                                                                    <span style={{ background: "#198754", color: "#fff", padding: "3px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>Confirmed</span>
                                                                </div>
                                                                <div className="table-responsive">
                                                                    <table className="table table-bordered" style={{ maxWidth: "800px" }}>
                                                                        <thead style={{ background: "#f8f9fa" }}>
                                                                            <tr>
                                                                                <th style={{ fontWeight: "600", fontSize: "13px" }}>Test Name</th>
                                                                                <th style={{ fontWeight: "600", fontSize: "13px" }}>Amount (₹)</th>
                                                                                <th style={{ fontWeight: "600", fontSize: "13px" }}>Payment Mode</th>
                                                                                <th style={{ fontWeight: "600", fontSize: "13px" }}>Payment Date</th>
                                                                                <th style={{ fontWeight: "600", fontSize: "13px" }}>Transaction ID</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td style={{ fontSize: "13px" }}>{caseDetails.paymentInfo.testName}</td>
                                                                                <td style={{ fontSize: "13px" }}>₹ {caseDetails.paymentInfo.amount}</td>
                                                                                <td style={{ fontSize: "13px" }}>{caseDetails.paymentInfo.paymentMode}</td>
                                                                                <td style={{ fontSize: "13px" }}>{caseDetails.paymentInfo.paymentDate ? caseDetails.paymentInfo.paymentDate.split("-").reverse().join("/") : ""}</td>
                                                                                <td style={{ fontSize: "13px" }}>{caseDetails.paymentInfo.paymentId}</td>
                                                                            </tr>
                                                                            {caseDetails?.additionalPaymentInfo?.paymentId && (
                                                                                <tr>
                                                                                    <td style={{ fontSize: "13px" }}>{caseDetails.additionalPaymentInfo.testName}</td>
                                                                                    <td style={{ fontSize: "13px" }}>₹ {caseDetails.additionalPaymentInfo.amount}</td>
                                                                                    <td style={{ fontSize: "13px" }}>{caseDetails.additionalPaymentInfo.paymentMode}</td>
                                                                                    <td style={{ fontSize: "13px" }}>{caseDetails.additionalPaymentInfo.paymentDate ? caseDetails.additionalPaymentInfo.paymentDate.split("-").reverse().join("/") : ""}</td>
                                                                                    <td style={{ fontSize: "13px" }}>{caseDetails.additionalPaymentInfo.paymentId}</td>
                                                                                </tr>
                                                                            )}
                                                                        </tbody>
                                                                        <tfoot>
                                                                            <tr style={{ background: "#f8f9fa" }}>
                                                                                <td style={{ fontWeight: "700", fontSize: "13px" }}>Total</td>
                                                                                <td style={{ fontWeight: "700", fontSize: "13px" }}>
                                                                                    ₹ {(parseFloat(caseDetails.paymentInfo.amount) || 0) + (caseDetails?.additionalPaymentInfo?.paymentId ? (parseFloat(caseDetails.additionalPaymentInfo.amount) || 0) : 0)}
                                                                                </td>
                                                                                <td colSpan="3" />
                                                                            </tr>
                                                                        </tfoot>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        ) : additionalTests.length > 0 && !additionaltestapprove ? (
                                                            <p className="text-muted">Please wait for the additional tests to be approved before confirming payment.</p>
                                                        ) : (
                                                            /* ── Payment pending — show confirmation form ── */
                                                            <div>
                                                                <h5 className="mb-4" style={{ fontWeight: "600", color: "#333" }}>Confirm Payment</h5>
                                                                <div className="row col-lg-12">
                                                                    {readOnlyField("TEST NAME", caseDetails?.defaultBiopsyTest?.name || "Biopsy")}
                                                                    {readOnlyField("AMOUNT (₹)", caseDetails?.defaultBiopsyTest?.cost || "1000")}

                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">PAYMENT MODE <span className="text-danger">*</span></label>
                                                                            <select
                                                                                className="form-control form-select"
                                                                                value={paymentMode}
                                                                                onChange={(e) => setPaymentMode(e.target.value)}
                                                                            >
                                                                                <option value="">Select Mode</option>
                                                                                <option value="Cash">Cash</option>
                                                                                <option value="UPI">UPI</option>
                                                                                <option value="Card">Card (Debit / Credit)</option>
                                                                                <option value="Net Banking">Net Banking</option>
                                                                                <option value="Cheque">Cheque</option>
                                                                                <option value="DD">Demand Draft (DD)</option>
                                                                            </select>
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">PAYMENT DATE <span className="text-danger">*</span></label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                value={paymentDate}
                                                                                onChange={(e) => setPaymentDate(e.target.value)}
                                                                                max={new Date().toISOString().split("T")[0]}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">PAYMENT / TRANSACTION ID <span className="text-danger">*</span></label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={paymentId}
                                                                                onChange={(e) => setPaymentId(e.target.value)}
                                                                                placeholder="Enter receipt / transaction reference"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-md-12 mt-2">
                                                                    <button
                                                                        className="create-quiz-btn border-0"
                                                                        onClick={handleConfirmPayment}
                                                                        disabled={isPaymentLoading}
                                                                    >
                                                                        {isPaymentLoading ? "Confirming…" : "Confirm Payment"}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {additionalTests.length > 0 && (
                                                            <div className="mt-4">
                                                                <div className="justify-content-between align-items-center mb-2">
                                                                    <label className="form_label mb-0 mr-2">ADDITIONAL TESTS</label>
                                                                    <span style={{
                                                                        background: additionaltestapprove ? "#198754" : "#ffc107",
                                                                        color: additionaltestapprove ? "#fff" : "#000",
                                                                        marginLeft:"10px",
                                                                        padding: "3px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600",
                                                                    }}>
                                                                        {additionaltestapprove ? "Approved" : "Pending Approval"}
                                                                    </span>
                                                                </div>
                                                                <table className="table table-bordered" style={{ maxWidth: "800px" }}>
                                                                    <thead style={{ background: "#f8f9fa" }}>
                                                                        <tr>
                                                                            <th style={{ fontWeight: "600", fontSize: "13px" }}>Test Name</th>
                                                                            <th style={{ fontWeight: "600", fontSize: "13px" }}>Fee (₹)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {additionalTests.map((t, index) => (
                                                                            <tr key={`${t.name}-${index}`}>
                                                                                <td style={{ fontSize: "13px" }}>{t.name}</td>
                                                                                <td style={{ fontSize: "13px" }}>{t.fee}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    <tfoot>
                                                                        <tr style={{ background: "#f8f9fa" }}>
                                                                            <td style={{ fontWeight: "700", fontSize: "13px" }}>Total</td>
                                                                            <td style={{ fontWeight: "700", fontSize: "13px" }}>
                                                                                ₹ {additionalTests.reduce((sum, t) => sum + (parseFloat(t.fee) || 0), 0)}
                                                                            </td>
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>

                                                                {additionaltestapprove && !caseDetails?.additionalPaymentInfo?.paymentId && (
                                                                    /* ── Additional tests payment pending — confirmation form (once confirmed, it merges into Payment Summary above) ── */
                                                                    <div className="mt-3">
                                                                            <h6 className="mb-3" style={{ fontWeight: "600", color: "#333" }}>Confirm Additional Tests Payment</h6>
                                                                            <div className="row col-lg-12">
                                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                                    <div className="mb-3 input-field">
                                                                                        <label className="form_label">PAYMENT MODE <span className="text-danger">*</span></label>
                                                                                        <select
                                                                                            className="form-control form-select"
                                                                                            value={addlPaymentMode}
                                                                                            onChange={(e) => setAddlPaymentMode(e.target.value)}
                                                                                        >
                                                                                            <option value="">Select Mode</option>
                                                                                            <option value="Cash">Cash</option>
                                                                                            <option value="UPI">UPI</option>
                                                                                            <option value="Card">Card (Debit / Credit)</option>
                                                                                            <option value="Net Banking">Net Banking</option>
                                                                                            <option value="Cheque">Cheque</option>
                                                                                            <option value="DD">Demand Draft (DD)</option>
                                                                                        </select>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                                    <div className="mb-3 input-field">
                                                                                        <label className="form_label">PAYMENT DATE <span className="text-danger">*</span></label>
                                                                                        <input
                                                                                            type="date"
                                                                                            className="form-control"
                                                                                            value={addlPaymentDate}
                                                                                            onChange={(e) => setAddlPaymentDate(e.target.value)}
                                                                                            max={new Date().toISOString().split("T")[0]}
                                                                                        />
                                                                                    </div>
                                                                                </div>

                                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                                    <div className="mb-3 input-field">
                                                                                        <label className="form_label">PAYMENT / TRANSACTION ID <span className="text-danger">*</span></label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={addlPaymentId}
                                                                                            onChange={(e) => setAddlPaymentId(e.target.value)}
                                                                                            placeholder="Enter receipt / transaction reference"
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <div className="col-md-12 mt-2">
                                                                                <button
                                                                                    className="create-quiz-btn border-0"
                                                                                    onClick={handleConfirmAdditionalPayment}
                                                                                    disabled={isAddlPaymentLoading}
                                                                                >
                                                                                    {isAddlPaymentLoading ? "Confirming…" : "Confirm Payment"}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {/* ── end payment tab ────── */}

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {/* ── clinical image lightbox ──────────────────────────────────────── */}
            {showImagePopup && (
                <div
                    style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.85)", zIndex: 1003, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
                    onClick={() => setShowImagePopup(false)}
                >
                    <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
                        {isClinicalLoading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader /></div>}
                        <img src={popupImageUrl} style={{ maxWidth: "85vw", maxHeight: "85vh", objectFit: "contain", display: "block" }} onLoad={() => setIsClinicalLoading(false)} alt="clinical" />
                        <button onClick={() => setShowImagePopup(false)} style={{ position: "absolute", top: "-10px", right: "-10px", background: "white", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", fontWeight: "bold" }}>×</button>
                        {clinicalPopupList.length > 1 && (
                            <>
                                <button onClick={() => navigateClinicalImage(clinicalPopupIndex - 1)} disabled={clinicalPopupIndex === 0} style={{ position: "absolute", left: "-40px", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: "24px", cursor: "pointer", borderRadius: "4px", padding: "4px 8px" }}>‹</button>
                                <button onClick={() => navigateClinicalImage(clinicalPopupIndex + 1)} disabled={clinicalPopupIndex === clinicalPopupList.length - 1} style={{ position: "absolute", right: "-40px", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: "24px", cursor: "pointer", borderRadius: "4px", padding: "4px 8px" }}>›</button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── DZI popup (non-DZI / small image with zoom-pan-pinch) ─────── */}
            {isPopupOpen && !dziUrl && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999 }} onClick={() => setPopupOpen(false)}>
                    <div className="popup_wrp" style={{ position: "relative", background: "white", padding: "10px", borderRadius: "10px", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
                        <button style={{ position: "absolute", top: 10, right: 10, background: "white", color: "#000", border: "none", cursor: "pointer", borderRadius: "5px" }} onClick={() => setPopupOpen(false)}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                        <TransformWrapper ref={transformRef} initialScale={1}>
                            <div style={{ textAlign: "left", marginBottom: "10px" }}>
                                <button onClick={() => transformRef.current.zoomIn()} className="btnzoom"><span className="material-symbols-outlined">add</span></button>
                                <button onClick={() => transformRef.current.zoomOut()} className="btnzoom"><span className="material-symbols-outlined">remove</span></button>
                                <button onClick={() => transformRef.current.resetTransform()} className="btnzoom"><span className="material-symbols-outlined">refresh</span></button>
                            </div>
                            <TransformComponent>
                                {isPopupLoading && <div style={{ width: "400px", height: "400px" }}><Loader /></div>}
                                <LazyLoadImage src={dzipopupImage} effect="blur" onLoad={() => setPopupLoading(false)} style={{ width: "100%", borderRadius: "10px" }} />
                            </TransformComponent>
                        </TransformWrapper>
                    </div>
                </div>
            )}

            {/* ── DZI popup (OpenSeadragon full-screen) ────────────────────── */}
            {isPopupOpen && dziUrl && (
                <div className="deep_zoom">
                    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", zIndex: 999 }}>
                        <div style={{ width: "98vw", height: "98vh", position: "relative", backgroundColor: "black", overflow: "hidden" }}>

                            {/* header bar */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 20px", height: "52px", background: "linear-gradient(135deg, #091918 0%, #215250 100%)", borderBottom: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                                <div style={{ width: "120px" }} />

                                {dziImageList.length > 1 ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "0", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "30px", padding: "3px" }}>
                                        <button onClick={() => navigateDziImage(dziCurrentIndex - 1)} disabled={dziCurrentIndex === 0} style={{ border: "none", background: dziCurrentIndex === 0 ? "transparent" : "rgba(255,255,255,0.1)", color: dziCurrentIndex === 0 ? "rgba(255,255,255,0.25)" : "#fff", cursor: dziCurrentIndex === 0 ? "not-allowed" : "pointer", width: "34px", height: "34px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>chevron_left</span>
                                        </button>
                                        <span style={{ display: "flex", alignItems: "center", gap: "4px", padding: "0 12px", fontSize: "13px", fontWeight: "600", color: "#fff", userSelect: "none" }}>
                                            <span style={{ color: "#EA7F4A", fontWeight: "700" }}>{dziCurrentIndex + 1}</span>
                                            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "11px" }}>/</span>
                                            <span style={{ color: "rgba(255,255,255,0.6)" }}>{dziImageList.length}</span>
                                        </span>
                                        <button onClick={() => navigateDziImage(dziCurrentIndex + 1)} disabled={dziCurrentIndex === dziImageList.length - 1} style={{ border: "none", background: dziCurrentIndex === dziImageList.length - 1 ? "transparent" : "rgba(255,255,255,0.1)", color: dziCurrentIndex === dziImageList.length - 1 ? "rgba(255,255,255,0.25)" : "#fff", cursor: dziCurrentIndex === dziImageList.length - 1 ? "not-allowed" : "pointer", width: "34px", height: "34px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>chevron_right</span>
                                        </button>
                                    </div>
                                ) : <div />}

                                <div style={{ width: "120px", display: "flex", justifyContent: "flex-end" }}>
                                    <button className="dz_close" onClick={closeDeepZoom} style={{ border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: "50%", width: "34px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>close</span>
                                    </button>
                                </div>
                            </div>

                            {/* OSD viewer */}
                            <div style={{ height: "calc(95vh - 80px)", width: "100%", position: "relative" }}>
                                <div id="openseadragon-viewer" ref={viewerRef} style={{ height: "100%", width: "100%" }} />
                                {isDziLoading && (
                                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)", zIndex: 20, pointerEvents: "none" }}>
                                        <div style={{ width: "52px", height: "52px", borderRadius: "50%", border: "4px solid rgba(255,255,255,0.15)", borderTopColor: "#EA7F4A", animation: "dz-spin 0.75s linear infinite" }} />
                                        <style>{`@keyframes dz-spin { to { transform: rotate(360deg); } }`}</style>
                                    </div>
                                )}
                                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "grey", fontSize: "2vw", fontWeight: "600", textTransform: "uppercase", letterSpacing: "2px", pointerEvents: "none", zIndex: 10, userSelect: "none" }}>
                                    Copyright © 2026 DADI
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
